// The spectate variant's playhead readouts (`tournament-big-screen` design **D10**,
// `client-rounds-review`'s "spectate variant" requirement).
//
// Both the eliminated counter and the rail's ordering are a lookup in the **coarse tier's
// nearest sample at or before the playhead** — `ReviewTrace.coarseAt(tick)` already implements
// exactly that lookup (`net/rounds-trace.ts`), so this module is the small amount of shaping
// between that sample and what the two readouts need: a count, and a `GradedColony[]` whose
// `score` tracks the playhead rather than the terminal tick. No detail-tier scan, no second
// read, no new trace data — the coarse tier is already buffered whole by the time a review
// screen has anything to draw.

import type { CoarseSample } from '../../net/rounds-trace';
import type { GradedColony } from '../components/rounds/ColonyRail';

/** How many colonies the sample at or before `tick` reports as no longer alive. `0` when there
 * is no sample yet (before the trace's first coarse tick) — not a fabricated "everyone alive". */
export function eliminatedCountAt(sample: CoarseSample | null): number {
  if (sample === null) {
    return 0;
  }
  return sample.colonies.reduce((count, colony) => (colony.alive ? count : count + 1), 0);
}

/**
 * The spectate rail's rows: **every colony the sample carries**, scored by its own living
 * population at the playhead, with `roster` supplying identity for the colonies that have one.
 *
 * The row set is the *sample's*, not the roster's, and that direction is the whole point. The
 * roster comes from the standings, bot colonies from the fill (**D8**) score nowhere and so are
 * absent from it — so a roster-driven rail can only ever list the humans. A bot-filled graded
 * round rehearsed end to end (task 11.6) showed exactly what that costs: the eliminated counter,
 * which reads this same sample, climbed to four while the rail beside it rendered one row, and
 * `ColonyRail`'s bot-labelling branch was unreachable from the composition root that feeds it.
 * A projected screen must not report a field it refuses to show.
 *
 * A colony the sample does not mention (before its first coarse tick) keeps its roster row and
 * its roster-declared score rather than being dropped — the rail should not blink empty for the
 * first sample interval, and the subject colony in particular must always have a row.
 *
 * Placement is carried through unused by the spectate row shape ({@link ColonyRail}'s
 * `orderBy: 'score'` renders no placement digit and orders by score), so roster placements pass
 * straight through and a bot row is given `0` — a value that is never rendered and never
 * compared, rather than an invented rank.
 */
export function spectateRailColonies(
  sample: CoarseSample | null,
  roster: readonly GradedColony[],
): readonly GradedColony[] {
  if (sample === null) {
    return roster;
  }
  const byColony = new Map(sample.colonies.map((entry) => [entry.colony, entry]));
  const named = new Map(roster.map((entry) => [entry.colony, entry]));
  const rows: GradedColony[] = roster.map((entry) => {
    const live = byColony.get(entry.colony);
    return live === undefined ? entry : { ...entry, score: live.population };
  });
  for (const entry of sample.colonies) {
    if (!named.has(entry.colony)) {
      rows.push({
        colony: entry.colony,
        displayName: null,
        score: entry.population,
        placement: 0,
        isBot: true,
      });
    }
  }
  return rows.sort((a, b) => a.colony - b.colony);
}
